<div class="terminal-settings">
  <app-collapsible-card
    class="terminal-settings__card"
    title="Terminal"
    [isCollapsed]="false"
    [isFixedOpen]="true"
  >
    <p class="terminal-settings__lead">
      Configure Stripe Connect, terminal readers, and checkout options for each location. Connect
      accounts use platform-managed Stripe settings.
    </p>

    <p class="terminal-settings__hint" *ngIf="locationOptions.length === 0">
      Add a location under Location Management before configuring terminal settings.
    </p>

    <div class="terminal-settings__toolbar" *ngIf="locationOptions.length > 0">
      <div class="terminal-settings__tabs-wrap">
        <div class="tab-container terminal-settings__tabs" *ngIf="selectedLocationId">
          <div class="tab-menu">
            <div
              *ngFor="let section of visibleSections"
              class="tab-item"
              [class.active]="activeSection === section"
              (click)="setActiveSection(section)"
            >
              <span>{{ section }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="terminal-settings__location-filter">
        <!-- <label class="terminal-settings__label" for="terminal-location-select">Location</label> -->
        <div class="terminal-settings__dropdown-wrap">
          <mat-select
            id="terminal-location-select"
            class="form-select"
            [(ngModel)]="selectedLocationId"
            placeholder="Select location…"
            panelClass="terminal-location-select-panel"
            disableOptionCentering
            (selectionChange)="onLocationChange()"
          >
            <!-- [hidden], not *ngIf: a template ref declared inside a structural
                 directive is scoped to that embedded view and would be invisible to
                 the sibling *ngFor below, leaving the filter permanently empty. -->
            <app-select-search
              #locationSearch
              [hidden]="locationOptions.length <= 5"
              placeholder="Search locations"
            ></app-select-search>
            <mat-option
              *ngFor="
                let location of locationOptions
                  | selectFilter: locationSearch.query : ['label', 'id']
              "
              [value]="location.id"
            >
              <span class="terminal-settings__option-text">{{ location.label }}</span>
            </mat-option>
          </mat-select>
        </div>
      </div>
    </div>

    <ng-container *ngIf="selectedLocationId">
      <!-- Account -->
      <div class="terminal-settings__section" *ngIf="activeSection === menuLabels.Account">
        <ng-container *ngIf="connectAccount; else accountSetup">
          <p class="terminal-settings__section-desc">
            Stripe Connect account linked to this location.
          </p>

          <dl class="terminal-settings__details">
            <div>
              <dt>Account ID</dt>
              <dd class="mono">{{ connectAccount.accountId }}</dd>
            </div>
            <div *ngIf="connectAccount.businessName">
              <dt>Business</dt>
              <dd>{{ connectAccount.businessName }}</dd>
            </div>
            <div *ngIf="connectAccount.email">
              <dt>Email</dt>
              <dd>{{ connectAccount.email }}</dd>
            </div>
            <div *ngIf="connectAccount.country">
              <dt>Country</dt>
              <dd>{{ connectAccount.country }}</dd>
            </div>
            <div>
              <dt>Charges</dt>
              <dd>
                <span
                  class="status-badge"
                  [class.status-badge--active]="connectAccount.chargesEnabled"
                >
                  {{ connectAccount.chargesEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </dd>
            </div>
            <div>
              <dt>Payouts</dt>
              <dd>
                <span
                  class="status-badge"
                  [class.status-badge--active]="connectAccount.payoutsEnabled"
                >
                  {{ connectAccount.payoutsEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </dd>
            </div>
            <div
              class="terminal-settings__details-full"
              *ngIf="connectAccount.requirementsCurrentlyDue > 0"
            >
              <dt>Requirements</dt>
              <dd class="warn">{{ connectAccount.requirementsCurrentlyDue }} item(s) pending</dd>
            </div>
          </dl>

          <div class="terminal-settings__actions">
            <button
              type="button"
              class="btn btn-primary btn-medium"
              (click)="editConnectAccount()"
              [disabled]="accountUpdateLinkLoading || deletingConnectAccount"
            >
              {{ accountUpdateLinkLoading ? 'Redirecting…' : 'Edit account' }}
            </button>
            <button
              type="button"
              class="btn btn-outlined btn-medium terminal-settings__delete-btn"
              (click)="openDeleteAccountDialog()"
              [disabled]="deletingConnectAccount || accountUpdateLinkLoading"
            >
              Delete account
            </button>
          </div>
        </ng-container>

        <ng-template #accountSetup>
          <ng-container *ngIf="!connectAccountLoading; else accountLoading">
            <p class="terminal-settings__section-desc">
              Link a Stripe Connect account to accept in-person terminal payments at this location.
            </p>

            <ul class="terminal-settings__list">
              <li>Connect your bank account for payouts</li>
              <li>Verify your business details</li>
              <li>Enable card payments on Stripe Terminal readers</li>
            </ul>

            <div class="terminal-settings__actions">
              <button
                type="button"
                class="btn btn-primary btn-medium"
                (click)="createConnectAccount()"
                [disabled]="stripeLinkLoading"
              >
                <img
                  *ngIf="!stripeLinkLoading"
                  src="assets/images/package/plus.svg"
                  alt=""
                  aria-hidden="true"
                />
                {{ stripeLinkLoading ? 'Redirecting…' : 'Create Connect Account' }}
              </button>
            </div>
          </ng-container>

          <ng-template #accountLoading>
            <div class="terminal-settings__details-skeleton" aria-hidden="true">
              <div
                class="terminal-settings__skeleton-field"
                *ngFor="let _ of accountSkeletonFields"
              >
                <div class="terminal-settings__skeleton-label"></div>
                <div class="terminal-settings__skeleton-value"></div>
              </div>
            </div>
          </ng-template>
        </ng-template>
      </div>

      <!-- Readers -->
      <div class="terminal-settings__section" *ngIf="activeSection === menuLabels.Readers">
        <div class="terminal-settings__section-header">
          <p class="terminal-settings__section-desc">
            Manage Stripe Terminal readers for this location.
          </p>
          <button
            type="button"
            class="btn btn-primary btn-medium"
            (click)="openConfigureReaderDialog()"
          >
            Link reader
          </button>
        </div>

        <div class="ledger-table-wrap">
          <div class="table-scroll-wrapper" *ngIf="readersLoading">
            <app-loading-table-shimmer
              [columns]="readerShimmerColumns"
              [rows]="4"
              [showHeader]="false"
              className="scrollable"
            >
            </app-loading-table-shimmer>
          </div>

          <div *ngIf="!readersLoading" class="table-scroll-wrapper">
            <table mat-table [dataSource]="readers" class="mat-mdc-table payments-table">
              <!-- Name Column -->
              <ng-container matColumnDef="label">
                <th mat-header-cell *matHeaderCellDef>Name</th>
                <td mat-cell *matCellDef="let reader">
                  <button
                    type="button"
                    class="terminal-settings__link"
                    (click)="openEditReaderDialog(reader)"
                  >
                    {{ reader.label || '—' }}
                  </button>
                </td>
              </ng-container>

              <!-- Device Type Column -->
              <ng-container matColumnDef="device_type">
                <th mat-header-cell *matHeaderCellDef>Device Type</th>
                <td mat-cell *matCellDef="let reader">{{ reader.device_type || '—' }}</td>
              </ng-container>

              <!-- Serial Number Column -->
              <ng-container matColumnDef="serial_number">
                <th mat-header-cell *matHeaderCellDef>Serial Number</th>
                <td mat-cell *matCellDef="let reader">{{ reader.serial_number || '—' }}</td>
              </ng-container>

              <!-- Status Column -->
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let reader">
                  <span
                    class="status-badge"
                    [class.status-badge--active]="reader.status === 'online'"
                  >
                    {{ reader.status === 'online' ? 'Online' : 'Offline' }}
                  </span>
                </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let reader">
                  <button
                    type="button"
                    class="terminal-settings__icon-btn"
                    aria-label="Delete reader"
                    (click)="openDeleteReaderDialog(reader)"
                  >
                    <img src="assets/images/trash.svg" alt="" width="16" height="16" />
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedReaderColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedReaderColumns"></tr>

              <!-- Empty State -->
              <tr *ngIf="readers.length === 0" class="mat-mdc-row terminal-settings__empty-row">
                <td [attr.colspan]="displayedReaderColumns.length" class="terminal-settings__empty">
                  No readers linked yet. Use "Link reader" to register a Stripe Terminal device.
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <!-- Settings -->
      <div class="terminal-settings__section" *ngIf="activeSection === menuLabels.Settings">
        <p class="terminal-settings__section-desc">
          Configure payment options for in-person terminal checkout.
        </p>

        <div class="terminal-settings__toggle-row">
          <div>
            <h3 class="terminal-settings__toggle-title">Convenience fee</h3>
            <p class="terminal-settings__toggle-desc">
              Pass Stripe processing fees to the customer at terminal checkout.
            </p>
          </div>
          <mat-slide-toggle [(ngModel)]="convenienceFee"></mat-slide-toggle>
        </div>

        <div class="terminal-settings__actions">
          <button
            type="button"
            class="btn btn-primary btn-medium"
            (click)="saveSettings()"
            [disabled]="loading"
          >
            {{ loading ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </ng-container>
  </app-collapsible-card>
</div>

<div class="modal-overlay" *ngIf="showAddReaderDialog" (click)="showAddReaderDialog = false">
  <div class="terminal-settings__modal" (click)="$event.stopPropagation()">
    <div class="terminal-settings__modal-header">
      <h2>Link reader</h2>
      <button
        type="button"
        class="close-button"
        (click)="showAddReaderDialog = false"
        aria-label="Close"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#555963"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="terminal-settings__modal-body">
      <div class="terminal-settings__form-field">
        <label for="terminal-registration-code">Registration code *</label>
        <input
          id="terminal-registration-code"
          matInput
          type="text"
          [(ngModel)]="registrationCode"
          placeholder="Enter code from reader or Stripe Dashboard"
        />
      </div>
      <div class="terminal-settings__form-field">
        <label for="terminal-reader-name">Reader name *</label>
        <input
          id="terminal-reader-name"
          matInput
          type="text"
          [(ngModel)]="readerName"
          maxlength="35"
          placeholder="e.g. Front counter"
        />
        <small class="terminal-settings__field-error" *ngIf="isReaderNameDuplicate(readerName)">
          A reader with this name already exists.
        </small>
      </div>
    </div>
    <div class="terminal-settings__modal-footer">
      <button
        type="button"
        class="btn btn-secondary btn-large"
        (click)="showAddReaderDialog = false"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-large"
        (click)="addReader()"
        [disabled]="
          addingReader ||
          !registrationCode.trim() ||
          !readerName.trim() ||
          isReaderNameDuplicate(readerName)
        "
      >
        {{ addingReader ? 'Linking…' : 'Link' }}
      </button>
    </div>
  </div>
</div>

<div class="modal-overlay" *ngIf="showEditReaderDialog" (click)="showEditReaderDialog = false">
  <div class="terminal-settings__modal" (click)="$event.stopPropagation()">
    <div class="terminal-settings__modal-header">
      <h2>Rename reader</h2>
      <button
        type="button"
        class="close-button"
        (click)="showEditReaderDialog = false"
        aria-label="Close"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#555963"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="terminal-settings__modal-body">
      <div class="terminal-settings__form-field">
        <label for="terminal-reader-name-edit">Reader name *</label>
        <input
          id="terminal-reader-name-edit"
          matInput
          type="text"
          [(ngModel)]="editReaderName"
          maxlength="35"
        />
        <small
          class="terminal-settings__field-error"
          *ngIf="isReaderNameDuplicate(editReaderName, editingReader?.id)"
        >
          A reader with this name already exists.
        </small>
      </div>
    </div>
    <div class="terminal-settings__modal-footer">
      <button
        type="button"
        class="btn btn-secondary btn-large"
        (click)="showEditReaderDialog = false"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-large"
        (click)="updateReader()"
        [disabled]="
          updatingReader ||
          !editReaderName.trim() ||
          isReaderNameDuplicate(editReaderName, editingReader?.id)
        "
      >
        {{ updatingReader ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </div>
</div>

<div class="modal-overlay" *ngIf="showDeleteAccountDialog" (click)="closeDeleteAccountDialog()">
  <div class="terminal-settings__modal" (click)="$event.stopPropagation()">
    <div class="terminal-settings__modal-header">
      <h2>Delete connected account?</h2>
      <button
        type="button"
        class="close-button"
        (click)="closeDeleteAccountDialog()"
        [disabled]="deletingConnectAccount"
        aria-label="Close"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#555963"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="terminal-settings__modal-body terminal-settings__modal-body--confirm">
      <p>
        Are you sure you want to delete the Stripe Connect account
        <strong>{{ connectAccount?.accountId }}</strong>
        from this location?
      </p>
      <p class="terminal-settings__warning-text">
        This will remove the connected account and unlink all terminal readers for this location.
        This action cannot be undone.
      </p>
    </div>
    <div class="terminal-settings__modal-footer">
      <button
        type="button"
        class="btn btn-secondary btn-large"
        (click)="closeDeleteAccountDialog()"
        [disabled]="deletingConnectAccount"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-large"
        (click)="confirmDeleteConnectAccount()"
        [disabled]="deletingConnectAccount"
      >
        {{ deletingConnectAccount ? 'Deleting…' : 'Delete account' }}
      </button>
    </div>
  </div>
</div>

<div class="modal-overlay" *ngIf="showDeleteReaderDialog" (click)="closeDeleteReaderDialog()">
  <div class="terminal-settings__modal" (click)="$event.stopPropagation()">
    <div class="terminal-settings__modal-header">
      <h2>Delete reader?</h2>
      <button
        type="button"
        class="close-button"
        (click)="closeDeleteReaderDialog()"
        [disabled]="deletingReader"
        aria-label="Close"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="#555963"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="terminal-settings__modal-body terminal-settings__modal-body--confirm">
      <p>
        Are you sure you want to remove
        <strong>{{ readerToDelete?.label || 'this reader' }}</strong>
        from your account? This action cannot be undone.
      </p>
    </div>
    <div class="terminal-settings__modal-footer">
      <button
        type="button"
        class="btn btn-secondary btn-large"
        (click)="closeDeleteReaderDialog()"
        [disabled]="deletingReader"
      >
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-large"
        (click)="confirmDeleteReader()"
        [disabled]="deletingReader"
      >
        {{ deletingReader ? 'Deleting…' : 'Delete' }}
      </button>
    </div>
  </div>
</div>
